import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  type Column,
} from '@eon/ui';
import { hooks, type AdminUserOrg, type UserIdTagRow } from '@eon/api-client';
import type { ChargingSession } from '@eon/types';
import { formatDate, formatDateTime, formatRelative } from '@eon/utils';
import { SessionStatusBadge } from '@/components/status/StatusBadges';
import { useClientTableSort, useTableSort } from '@/lib/table-sort';
import { ArrowLeft, KeyRound, Pencil, Trash2 } from 'lucide-react';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

export function UserDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams({ from: '/_app/users/$userId' });
  const { data: user, isLoading } = hooks.useAdminUser(userId);
  const { data: idTags = [], isLoading: tagsLoading } = hooks.useAdminUserIdTags(userId);
  const { sort: sessionSort, onSortChange: onSessionSortChange, sortParams } = useTableSort();
  const { data: sessionsPage, isLoading: sessionsLoading } = hooks.useAdminUserSessions(userId, {
    page: 1,
    pageSize: 20,
    ...sortParams,
  });
  const del = hooks.useDeleteUser();
  const forgot = hooks.useForgotPassword();

  const displayName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.email || userId;
  const addr = (user?.address ?? {}) as Record<string, string>;
  const sessions = sessionsPage?.data ?? [];

  const orgColumns: Column<AdminUserOrg>[] = [
    {
      key: 'name',
      header: t('group.name'),
      sortable: true,
      cell: (o) => (
        <Link to="/groups/$groupId" params={{ groupId: o.id }} className="font-medium hover:underline">
          {o.name}
        </Link>
      ),
    },
    {
      key: 'role',
      header: t('user.role.label'),
      sortable: true,
      cell: (o) => {
        const key = `user.role.${o.role}`;
        const label = t(key);
        return label === key ? o.role : label;
      },
    },
  ];

  const tagColumns: Column<UserIdTagRow>[] = [
    { key: 'tag', header: t('idTag.tag'), sortable: true, sortValue: (row) => row.tag, cell: (row) => <span className="font-mono text-xs">{row.tag}</span> },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      sortValue: (row) => row.isBlocked,
      cell: (row) => (
        <Badge variant={row.isBlocked ? 'destructive' : 'success'}>
          {row.isBlocked ? t('user.idTag.blocked') : t('user.idTag.active')}
        </Badge>
      ),
    },
    {
      key: 'type',
      header: t('user.idTag.type'),
      cell: (row) => (row.isPhysical ? t('user.idTag.physical') : t('user.idTag.virtual')),
    },
    {
      key: 'expires',
      header: t('idTag.expiry'),
      sortable: true,
      sortValue: (row) => row.expiresAt,
      cell: (row) => (row.expiresAt ? formatDate(row.expiresAt) : '—'),
    },
  ];

  const sessionColumns: Column<ChargingSession>[] = [
    {
      key: 'id',
      header: 'ID',
      cell: (s) => <span className="font-mono text-xs">{s.id.slice(0, 8)}…</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      cell: (s) => <SessionStatusBadge status={String(s.status)} />,
    },
    {
      key: 'energy',
      header: t('session.energy'),
      sortable: true,
      sortValue: (s) => s.totalEnergyKWh,
      cell: (s) => (s.totalEnergyKWh != null ? `${s.totalEnergyKWh.toFixed(2)} kWh` : '—'),
    },
    {
      key: 'started',
      header: t('session.started'),
      sortable: true,
      sortKey: 'started',
      sortValue: (s) => s.startedAt,
      cell: (s) => formatDateTime(s.startedAt),
    },
  ];

  const orgs = user?.organizations ?? [];
  const {
    sort: orgSort,
    onSortChange: onOrgSortChange,
    sortedData: sortedOrgs,
  } = useClientTableSort(orgs, orgColumns);
  const {
    sort: tagSort,
    onSortChange: onTagSortChange,
    sortedData: sortedIdTags,
  } = useClientTableSort(idTags, tagColumns);

  const onDelete = () => {
    if (!user || !window.confirm(`"${displayName}"`)) return;
    del.mutate(user.id, {
      onSuccess: () => {
        toast.success(t('actions.delete'));
        navigate({ to: '/users' });
      },
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const onPasswordReset = () => {
    const email = user?.email?.trim();
    if (!email) {
      toast.error(t('user.resetPassword.noEmail'));
      return;
    }
    if (!window.confirm(`${t('user.resetPassword.confirm')} (${email})`)) return;
    forgot.mutate(
      { email },
      {
        onSuccess: () => toast.success(t('user.resetPassword.sent')),
        onError: (e) => toast.error(resolveApiError(e, t)),
      },
    );
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('nav.users')} />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <PageHeader title={t('user.notFound')} />
        <Button variant="outline" asChild>
          <Link to="/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('user.back')}
          </Link>
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={displayName}
        description={user.email ?? user.phone ?? undefined}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/users">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('user.back')}
              </Link>
            </Button>
            <Button variant="outline" onClick={onPasswordReset} disabled={forgot.isPending}>
              <KeyRound className="mr-2 h-4 w-4" />
              {t('user.resetPassword.action')}
            </Button>
            <Button variant="outline" asChild>
              <Link to="/users/$userId/edit" params={{ userId: user.id }}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Link>
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={del.isPending}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('actions.delete')}
            </Button>
          </>
        }
      />

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">{t('user.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="groups">{t('user.tabs.groups')}</TabsTrigger>
          <TabsTrigger value="idTags">{t('user.tabs.idTags')}</TabsTrigger>
          <TabsTrigger value="sessions">{t('user.tabs.sessions')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>{t('user.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow label={t('user.name')} value={displayName} />
              <InfoRow label={t('user.email')} value={user.email} />
              <InfoRow label={t('user.phone')} value={user.phone} />
              <InfoRow
                label={t('user.role.label')}
                value={
                  <Badge variant="secondary">
                    {t(`user.role.${user.role}`) === `user.role.${user.role}` ? user.role : t(`user.role.${user.role}`)}
                  </Badge>
                }
              />
              <InfoRow
                label={t('user.appType.label')}
                value={
                  <Badge variant="outline">
                    {t(`user.appType.${user.appType}`) === `user.appType.${user.appType}`
                      ? user.appType
                      : t(`user.appType.${user.appType}`)}
                  </Badge>
                }
              />
              <InfoRow
                label={t('common.status')}
                value={
                  <Badge variant={user.active ? 'success' : 'secondary'}>
                    {user.active ? t('user.active') : t('user.inactive')}
                  </Badge>
                }
              />
              <InfoRow label={t('device.lastSeen')} value={formatRelative(user.lastLoginAt)} />
              <InfoRow label={t('user.createdAt')} value={formatDateTime(user.createdAt)} />
              <InfoRow label={t('user.updatedAt')} value={user.updatedAt ? formatDateTime(user.updatedAt) : '—'} />
              <InfoRow label="ID" value={<span className="font-mono text-xs">{user.id}</span>} />
              {(addr.street || addr.city) && (
                <InfoRow
                  label={t('user.addressSection')}
                  value={[addr.street, addr.city, addr.state, addr.country, addr.postalCode]
                    .filter(Boolean)
                    .join(', ')}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups">
          <DataTable
            data={sortedOrgs}
            columns={orgColumns}
            rowKey={(o) => o.id}
            emptyTitle={t('user.groups.empty')}
            emptyDescription=""
            sort={orgSort}
            onSortChange={onOrgSortChange}
          />
        </TabsContent>

        <TabsContent value="idTags">
          <DataTable
            data={sortedIdTags}
            columns={tagColumns}
            loading={tagsLoading}
            rowKey={(row) => row.id}
            emptyTitle={t('user.idTags.empty')}
            emptyDescription=""
            sort={tagSort}
            onSortChange={onTagSortChange}
          />
        </TabsContent>

        <TabsContent value="sessions">
          <DataTable
            data={sessions}
            columns={sessionColumns}
            loading={sessionsLoading}
            rowKey={(s) => s.id}
            emptyTitle={t('user.sessions.empty')}
            emptyDescription=""
            sort={sessionSort}
            onSortChange={onSessionSortChange}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}
